// assets/kc-product.js — Kitcellar product-page components (personalisation, size guide, delivery estimate)
import { Component } from '@theme/component';
import { KcPrint } from './kc-print.js';

/**
 * @typedef {object} PersonaliseRefs
 * @property {HTMLElement} panel
 * @property {HTMLFieldSetElement} fields
 * @property {HTMLInputElement} nameInput
 * @property {HTMLInputElement} numberInput
 * @property {HTMLElement} [shirt]
 * @property {HTMLScriptElement} [printConfig]
 */

/**
 * Name & number personalisation. Inputs live in a fieldset that stays disabled until the
 * shopper chooses "Customise", so nothing is added to the cart line otherwise.
 * Kits with a print config get a live preview drawn by KcPrint on the kit's back shot.
 * @extends Component<PersonaliseRefs>
 */
class KcPersonalise extends Component {
  requiredRefs = ['panel', 'fields', 'nameInput', 'numberInput'];

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {KcPrint | undefined} */
  #print;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    // Browsers restore the checked radio on back/forward navigation; re-sync the panel to it
    window.addEventListener('pageshow', () => this.#sync(), { signal: this.#abort.signal });
    window.addEventListener('resize', () => this.#print?.fit(), { signal: this.#abort.signal });
    this.#sync();
  }

  /** The renderer for the current preview markup (rebuilt if a section re-render replaced it). */
  #printer() {
    const { shirt, printConfig } = this.refs;
    if (!shirt || !printConfig) return undefined;
    if (this.#print?.shirt !== shirt) {
      try { this.#print = new KcPrint(shirt, JSON.parse(printConfig.textContent || '{}')); }
      catch { this.#print = undefined; }
    }
    return this.#print;
  }

  #updatePreview() {
    const name = this.refs.nameInput?.value || '';
    const num = this.refs.numberInput?.value || '';
    this.#printer()?.setText(name, num);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /** @param {Event} event */
  handleToggle(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    this.#apply(input.value === 'yes');
    if (input.value === 'yes') this.refs.nameInput.focus({ preventScroll: true });
  }

  /** Match panel/fieldset state to whichever toggle radio is checked. */
  #sync() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('.kc-personalise__toggle input:checked'));
    this.#apply(checked?.value === 'yes');
    const font = /** @type {HTMLInputElement | null} */ (this.querySelector('.kc-personalise__fonts input:checked'));
    if (font) this.#printer()?.setFont(Number(font.dataset.index) || 0);
    this.#updatePreview();
  }

  /** @param {boolean} on */
  #apply(on) {
    const { panel, fields } = this.refs;
    if (!panel || !fields) return;
    panel.hidden = !on;
    fields.disabled = !on;
    // The name is fitted against the shirt's width, which is 0 while the panel is hidden
    if (on) this.#printer()?.fit();
  }

  handleName() {
    const { nameInput } = this.refs;
    const cleaned = nameInput.value.toUpperCase().replace(/[^A-ZÀ-ÖØ-Þ .'-]/g, '');
    if (cleaned !== nameInput.value) nameInput.value = cleaned;
    this.#updatePreview();
  }

  handleNumber() {
    const { numberInput } = this.refs;
    const cleaned = numberInput.value.replace(/\D/g, '').slice(0, 2);
    if (cleaned !== numberInput.value) numberInput.value = cleaned;
    this.#updatePreview();
  }

  /** @param {Event} event */
  handleFont(event) {
    const input = /** @type {HTMLInputElement} */ (event.target);
    this.#printer()?.setFont(Number(input.dataset.index) || 0);
  }
}

if (!customElements.get('kc-personalise-component')) customElements.define('kc-personalise-component', KcPersonalise);

/**
 * @typedef {object} SizeGuideRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLInputElement} height
 * @property {HTMLInputElement} weight
 * @property {HTMLSelectElement} fit
 * @property {HTMLElement} result
 */

/**
 * Size guide dialog with tabbed tables and a simple height/weight fit finder.
 * @extends Component<SizeGuideRefs>
 */
class KcSizeGuide extends Component {
  requiredRefs = ['dialog'];

  /** @param {Event} event */
  open(event) {
    const trigger = /** @type {HTMLElement} */ (event.target);
    const tab = trigger?.getAttribute('data-open-tab');
    if (tab) this.#select(tab);
    this.refs.dialog.showModal();
  }

  close() {
    this.refs.dialog.close();
  }

  /** @param {MouseEvent} event */
  handleBackdrop(event) {
    // event.target is re-pointed at the on:click element; the real click target is first in the path
    if (event.composedPath()[0] === this.refs.dialog) this.close();
  }

  /** @param {Event} event */
  handleTab(event) {
    const button = /** @type {HTMLElement} */ (event.target);
    this.#select(button.getAttribute('data-tab') || '');
  }

  /** @param {KeyboardEvent} event */
  handleTabKey(event) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
    const tabs = this.refs.tabs || [];
    const current = tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    const next = tabs[(current + (event.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
    if (!next) return;
    this.#select(next.getAttribute('data-tab') || '');
    next.focus();
  }

  /** @param {string} name */
  #select(name) {
    for (const tab of this.refs.tabs || []) {
      const on = tab.getAttribute('data-tab') === name;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
    }
    for (const panel of this.refs.panels || []) panel.hidden = panel.getAttribute('data-panel') !== name;
  }

  findSize() {
    const { height, weight, fit, result } = this.refs;
    if (!height || !weight || !result) return;
    const h = Number(height.value);
    const w = Number(weight.value);
    if (!h || !w) {
      result.textContent = result.getAttribute('data-empty') || '';
      return;
    }
    const sizes = ['S', 'M', 'L', 'XL', 'XXL', '3XL', '4XL'];
    const limits = [62, 73, 84, 95, 106, 118];
    let i = limits.findIndex((limit) => w < limit);
    if (i === -1) i = sizes.length - 1;
    if (h >= 188 && i < sizes.length - 1) i += 1;
    if (h < 165 && w < 73 && i > 0) i -= 1;
    if (fit?.value === 'loose' && i < sizes.length - 1) i += 1;
    const playerHint = result.getAttribute('data-player-hint') || '';
    const template = result.getAttribute('data-template') || '[size]';
    result.textContent = `${template.replace('[size]', sizes[i])} ${playerHint}`.trim();
  }
}

if (!customElements.get('kc-size-guide-component')) customElements.define('kc-size-guide-component', KcSizeGuide);

/**
 * Estimated delivery window in business days from today, formatted in the shopper's locale.
 */
class KcDeliveryEstimate extends HTMLElement {
  connectedCallback() {
    const min = Number(this.dataset.min || 0);
    const max = Number(this.dataset.max || 0);
    if (!min || !max) return;
    const fmt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { month: 'short', day: 'numeric' });
    this.textContent = `${fmt.format(addBusinessDays(new Date(), min))} – ${fmt.format(addBusinessDays(new Date(), max))}`;
  }
}

/** @param {Date} date @param {number} days */
function addBusinessDays(date, days) {
  const d = new Date(date);
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return d;
}

if (!customElements.get('kc-delivery-estimate')) customElements.define('kc-delivery-estimate', KcDeliveryEstimate);
